import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from '../api/client';
import { sessionStore, type SessionState, type SessionUser } from './session-store';
import {
  abilitiesOf,
  can as policyCan,
  conditionalSettingFor,
  type Ability,
  type AbilityRequirement,
  type PermissionOverlay,
  type PolicyResource,
} from './policy';
import { useClientConfig } from '../features/church/use-client-config';

/**
 * The session, as components see it (docs/07-implementation-roadmap.md F1.1, F1.2,
 * F1.4).
 *
 * The state itself lives in `session-store.ts`, outside React, because the
 * refresh middleware has to reach it from a fetch callback. These hooks are the
 * bridge: `useSyncExternalStore` keeps every component consistent with the store
 * without a context value threaded through providers, and without the tearing a
 * hand-rolled subscription would produce under concurrent rendering.
 *
 * Split from `session.tsx` so that file exports only the provider component —
 * a module mixing components and plain functions defeats fast refresh.
 */

export function useSessionState(): SessionState {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.getState, sessionStore.getState);
}

/** The signed-in user, or `null`. Screens inside `RequireAuth` always have one. */
export function useCurrentUser(): SessionUser | null {
  return useSessionState().session?.user ?? null;
}

/**
 * The one way a component asks whether it may do something.
 *
 * A screen calls `can('members.delete', member)` — never `role === 'STAFF'`.
 * The answer comes from `policy.ts` and nowhere else (docs/07 §1.3), so when
 * F7.9 revisits who may do what, no component has to be opened.
 *
 * Two things that can move an answer are resolved here rather than by the
 * caller, because a screen asking "may I change this member's status?" should
 * not also have to know either of them:
 *
 *  - **The church settings that widen the matrix** for one named role.
 *  - **The church's configured permissions** (docs/07 P4), which are whatever
 *    the Administrator has turned on or off for the signed-in user's role.
 *    `client-config` carries only *this caller's* row, which is all `can()`
 *    ever asks about.
 *
 * `useClientConfig` is a session-long cached query (F1.9), so both cost no
 * request and `can()` stays synchronous — a hook that returned a pending state
 * would need a third rendering of the sidebar, every row action and every
 * dialog (docs/07 P4.3).
 *
 * **While that query is in flight the answer is the un-overridden one**, and
 * that is the safe direction in both cases. A pastor briefly sees "request"
 * rather than a "finalize" button that would 403; a coordinator whose church
 * has granted them notes briefly sees no composer rather than one that fails.
 * The reverse — showing a control and withdrawing it — is what teaches staff
 * that buttons sometimes lie.
 */
export function useCan(): (ability: Ability, resource?: PolicyResource) => boolean {
  const user = useCurrentUser();
  const { data: config } = useClientConfig();

  return useCallback(
    (ability, resource) => {
      const setting = conditionalSettingFor(ability);
      return policyCan(user, ability, resource, {
        conditionalRolesEnabled: setting ? config?.[setting] === true : false,
        permissions: config?.permissions as PermissionOverlay | undefined,
      });
    },
    [user, config],
  );
}

/**
 * The same question over a requirement that names more than one ability — a
 * screen two different jobs reach by two different rules (`AbilityRequirement`).
 * No route names more than one today; `/wilayah` did, and the shape is kept
 * because the single-ability case is just the one-element case of it.
 *
 * Built on `useCan` rather than on `policy.canAny` so that each ability is asked
 * with its *own* conditional setting resolved: `conditionalSettingFor` is
 * per-ability, and a single options object computed for the list would answer
 * one of them against another's flag.
 */
export function useCanAny(): (requirement: AbilityRequirement, resource?: PolicyResource) => boolean {
  const can = useCan();

  return useCallback(
    (requirement, resource) => abilitiesOf(requirement).some((ability) => can(ability, resource)),
    [can],
  );
}

/**
 * Sign out (F1.4).
 *
 * The local session is cleared **whatever the server says**. `POST /auth/logout`
 * is idempotent (docs/04 §1.2) and revoking the refresh token is the part that
 * matters server-side, but a staff member who clicked "Keluar" on a shared
 * office machine has to end up signed out even if that call fails — the opposite
 * default leaves someone's session open on a machine they have walked away from.
 */
export function useSignOut(): () => Promise<void> {
  const queryClient = useQueryClient();

  // Stable across renders: the idle timer (F1.11) holds this in an effect
  // dependency, and a new function identity every render would tear down and
  // rebuild its interval on each tick.
  return useCallback(async () => {
    try {
      await unwrap(api.POST('/api/v1/auth/logout', { body: {} }));
    } catch {
      // Deliberately swallowed — see above.
    } finally {
      sessionStore.clear();
      // Every cached query was answered for the person who just left. Clearing
      // rather than invalidating means the next user of this browser cannot see
      // a stale member record flash in before the refetch lands.
      queryClient.clear();
    }
  }, [queryClient]);
}
